import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { Switch } from "antd";
import axiosInstance from "../../../axios/axios";
import { Encrypt_Value, Decrypt_Value } from "../../../MiddleWare/EncryptDecrypt";

export default function TwilioSettingsConfigModal({
  show,
  onClose,
  twilioPayload,
  onSaveComplete,
  vendor
}) {
  const [phoneCallEnabled, setPhoneCallEnabled] = useState(false);
  const [incomingEnabled, setIncomingEnabled] = useState(false);
  const [outgoingEnabled, setOutgoingEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [provisionStatus, setProvisionStatus] = useState({
    step: 'idle', // 'idle' | 'ml_sid_loading' | 'api_key_loading' | 'done'
    twimlApp: null, // null | true | false
    apiKey: null, // null | true | false
    message: ''
  });
  const [incomingProvisionStatus, setIncomingProvisionStatus] = useState({
    step: 'idle', // 'idle' | 'phone_number_sid_loading' | 'update_webhook_loading' | 'done'
    phoneNumberSid: null, // null | true | false
    webhookUpdated: null, // null | true | false
    message: ''
  });

  const [errorMsg, setErrorMsg] = useState("");

  const SpinnerIcon = () => (
    <svg viewBox="0 0 50 50" style={{ width: '16px', height: '16px', animation: 'spin 1s linear infinite', marginRight: '8px' }}>
      <circle cx="25" cy="25" r="20" fill="none" stroke="#e5e7eb" strokeWidth="5"></circle>
      <circle cx="25" cy="25" r="20" fill="none" stroke="#2563eb" strokeWidth="5" strokeDasharray="31.4 100" strokeLinecap="round"></circle>
      <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
    </svg>
  );

  const CheckIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="#27ae60" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', marginRight: '8px' }}>
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
  );

  const CrossIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="#F24822" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '16px', height: '16px', marginRight: '8px' }}>
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  );

  useEffect(() => {
    if (show) {
      axiosInstance.get("/vozo/settingphonecall").then(res => {
        if (res.data) {
          const data = Decrypt_Value(res.data, "vozo");
          if (data && data.phone_call_enabled === "1") setPhoneCallEnabled(true);
          if (data && data.incoming_calls_enabled === "1") setIncomingEnabled(true);
          if (data && data.outgoing_calls_enabled === "1") setOutgoingEnabled(true);
        }
      }).catch(console.error);
    }
  }, [show]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const promises = [];
      if (twilioPayload) {
        const encryptedTwilio = Encrypt_Value(twilioPayload, "vozo");
        promises.push(axiosInstance.put("/vozo/settingsecurity", encryptedTwilio));
      }

      const phonePayload = {
        phone_call_enabled: phoneCallEnabled ? "1" : "0",
        incoming_calls_enabled: (phoneCallEnabled && incomingEnabled) ? "1" : "0",
        outgoing_calls_enabled: (phoneCallEnabled && outgoingEnabled) ? "1" : "0"
      };
      promises.push(axiosInstance.put("/vozo/settingphonecall", Encrypt_Value(phonePayload, "vozo")));

      await Promise.allSettled(promises);

      setLoading(false);
      onSaveComplete();
    } catch (e) {
      console.error(e);
      setLoading(false);
      onSaveComplete(); // Force complete even on error so user isn't stuck
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered dialogClassName="twilio-settings-modal" style={{ zIndex: 1060 }}>
      <Modal.Header closeButton className="p-8">
        <Modal.Title style={{ fontSize: "16px", fontWeight: "600" }}>Phone Call Configuration</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: "20px" }}>
        {errorMsg && (
          <div style={{ color: "#F24822", marginBottom: "15px", fontSize: "13px", padding: "8px", backgroundColor: "#F248221A", borderRadius: "4px" }}>
            {errorMsg}
          </div>
        )}

        <h5 style={{ fontWeight: 600, marginBottom: "15px", fontSize: "15px" }}>Phone Call Appointment</h5>
        <div style={{ display: "flex", alignItems: "center", marginBottom: "20px" }}>
          <span style={{ fontWeight: 500, marginRight: "10px", color: "#555", minWidth: "40px" }}>Call</span>
          <Switch checked={phoneCallEnabled} onChange={(c) => {
            setPhoneCallEnabled(c);
            if (!c) {
              setIncomingEnabled(false);
              setOutgoingEnabled(false);
            }
          }} style={{ marginRight: "10px" }} />
          <span style={{ color: "#555" }}>Enable</span>
        </div>

        <div style={{ marginTop: "15px" }}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
            <span style={{ fontWeight: 500, color: "#555" }}>Phone Call option</span>
          </div>

          <div style={{ display: "flex", flexDirection: "column", marginBottom: "12px", marginLeft: "5px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Switch checked={outgoingEnabled} disabled={!phoneCallEnabled || provisionStatus.step === 'ml_sid_loading' || provisionStatus.step === 'api_key_loading'} onChange={async (c) => {
                if (!phoneCallEnabled) return;
                // console.log('TWILIO_PROVISION: Outgoing toggle clicked. New state:', c);
                setOutgoingEnabled(c);
                if (c) {
                  setProvisionStatus({ step: 'ml_sid_loading', twimlApp: null, apiKey: null, message: '' });
                  try {
                    // console.log('TWILIO_PROVISION: Calling backend API for ml_sid...');
                    const mlRes = await axiosInstance.post("/vozo/twilio/register_outgoing_calls", { type: "ml_sid" });
                    
                    // Decrypt the backend response
                    const decryptedMlRes = Decrypt_Value(mlRes.data, "vozo");
                    // console.log('TWILIO_PROVISION: ml_sid decrypted API response:', decryptedMlRes);
                    
                    const twimlSuccess = decryptedMlRes && decryptedMlRes.status === "success";

                    if (!twimlSuccess) {
                      // console.error('TWILIO_PROVISION: TwiML App creation failed!');
                      setProvisionStatus({ step: 'done', twimlApp: false, apiKey: null, message: "Failed to configure TwiML App." });
                      return;
                    }

                    // console.log('TWILIO_PROVISION: TwiML App creation successful! Proceeding to api_key...');
                    setProvisionStatus({ step: 'api_key_loading', twimlApp: true, apiKey: null, message: '' });

                    // console.log('TWILIO_PROVISION: Calling backend API for api_key...');
                    const apiRes = await axiosInstance.post("/vozo/twilio/register_outgoing_calls", { type: "api_key" });
                    
                    // Decrypt the backend response
                    const decryptedApiRes = Decrypt_Value(apiRes.data, "vozo");
                    // console.log('TWILIO_PROVISION: api_key decrypted API response:', decryptedApiRes);
                    
                    const apiKeySuccess = decryptedApiRes && decryptedApiRes.status === "success";

                    if (!apiKeySuccess) {
                      // console.error('TWILIO_PROVISION: API Key creation failed!');
                      setProvisionStatus({ step: 'done', twimlApp: true, apiKey: false, message: "Failed to configure API Key." });
                      return;
                    }

                    // console.log('TWILIO_PROVISION: Everything successful!');
                    setProvisionStatus({
                      step: 'done',
                      twimlApp: true,
                      apiKey: true,
                      message: "Outgoing call configured successfully"
                    });

                    // Now that the TwiML App and API Key exist, notify App.js to fetch the token!
                    // console.log('TWILIO_PROVISION: Outgoing setup complete. Dispatching reinit-twilio-device event.');
                    window.dispatchEvent(new CustomEvent('reinit-twilio-device'));
                  } catch (err) {
                    // console.error('TWILIO_PROVISION: Network or server error during provisioning:', err);
                    setProvisionStatus({ step: 'done', twimlApp: false, apiKey: false, message: "Failed to connect to backend API." });
                  }
                } else {
                  setProvisionStatus({ step: 'idle', twimlApp: null, apiKey: null, message: '' });
                }
              }} style={{ marginRight: "10px" }} />
              <span style={{ color: phoneCallEnabled ? "#555" : "#aaa" }}>Outgoing Call</span>
            </div>

            {outgoingEnabled && provisionStatus.step !== 'idle' && (
              <div style={{ marginLeft: "45px", marginTop: "10px", fontSize: "14px" }}>

                {/* TwiML App Status */}
                <div style={{ display: "flex", alignItems: "center", marginBottom: "6px" }}>
                  {provisionStatus.step === 'ml_sid_loading' ? <SpinnerIcon /> : provisionStatus.twimlApp ? <CheckIcon /> : <CrossIcon />}
                  <span style={{ color: provisionStatus.step === 'ml_sid_loading' ? "#555" : provisionStatus.twimlApp ? "#333" : "#F24822" }}>TwiML App SID</span>
                </div>

                {/* API Key Status (Only show if TwiML succeeded or is loading API key) */}
                {(provisionStatus.twimlApp || provisionStatus.step === 'api_key_loading') && (
                  <>
                    <div style={{ display: "flex", alignItems: "center", marginBottom: "6px" }}>
                      {provisionStatus.step === 'api_key_loading' ? <SpinnerIcon /> : provisionStatus.apiKey ? <CheckIcon /> : <CrossIcon />}
                      <span style={{ color: provisionStatus.step === 'api_key_loading' ? "#555" : provisionStatus.apiKey ? "#333" : "#F24822" }}>API Key SID</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", marginBottom: "12px" }}>
                      {provisionStatus.step === 'api_key_loading' ? <SpinnerIcon /> : provisionStatus.apiKey ? <CheckIcon /> : <CrossIcon />}
                      <span style={{ color: provisionStatus.step === 'api_key_loading' ? "#555" : provisionStatus.apiKey ? "#333" : "#F24822" }}>API Key Secret</span>
                    </div>
                  </>
                )}

                {/* Overall Status Message */}
                {provisionStatus.step === 'done' && (
                  <div style={{ fontSize: "12px", color: provisionStatus.twimlApp && provisionStatus.apiKey ? "#27ae60" : "#F24822" }}>
                    {provisionStatus.message}
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", marginLeft: "5px" }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Switch checked={incomingEnabled} disabled={!phoneCallEnabled || incomingProvisionStatus.step === 'phone_number_sid_loading' || incomingProvisionStatus.step === 'update_webhook_loading'} onChange={async (c) => {
                if (!phoneCallEnabled) return;
                // console.log('TWILIO_PROVISION: Incoming toggle clicked. New state:', c);
                setIncomingEnabled(c);
                if (c) {
                  setIncomingProvisionStatus({ step: 'phone_number_sid_loading', phoneNumberSid: null, webhookUpdated: null, message: '' });
                  try {
                    // console.log('TWILIO_PROVISION: Calling backend API for phone_number_sid...');
                    const sidRes = await axiosInstance.post("/vozo/twilio/register_incoming_calls", { type: "phone_number_sid" });
                    // console.log('TWILIO_PROVISION: phone_number_sid raw API response:', sidRes.data);

                    // Decrypt the backend response
                    const decryptedSidRes = Decrypt_Value(sidRes.data, "vozo");
                    // console.log('TWILIO_PROVISION: phone_number_sid decrypted API response:', decryptedSidRes);

                    const sidSuccess = decryptedSidRes && decryptedSidRes.status === "success";

                    if (!sidSuccess) {
                      // console.error('TWILIO_PROVISION: Phone Number SID creation failed! Response:', decryptedSidRes);
                      setIncomingProvisionStatus({ step: 'done', phoneNumberSid: false, webhookUpdated: null, message: decryptedSidRes?.message || "Failed to get Phone Number SID." });
                      return;
                    }

                    // If already_configured is true, SID existed and webhook was already done previously
                    if (decryptedSidRes.already_configured) {
                      // console.log('TWILIO_PROVISION: Incoming calls already fully configured!');
                      setIncomingProvisionStatus({
                        step: 'done',
                        phoneNumberSid: true,
                        webhookUpdated: true,
                        message: "Incoming call configured successfully"
                      });
                      return;
                    }

                    // console.log('TWILIO_PROVISION: Phone Number SID successful! Proceeding to update_webhook...');
                    setIncomingProvisionStatus({ step: 'update_webhook_loading', phoneNumberSid: true, webhookUpdated: null, message: '' });

                    // console.log('TWILIO_PROVISION: Calling backend API for update_webhook...');
                    const webhookRes = await axiosInstance.post("/vozo/twilio/register_incoming_calls", { type: "update_webhook" });
                    // console.log('TWILIO_PROVISION: update_webhook raw API response:', webhookRes.data);

                    // Decrypt the backend response
                    const decryptedWebhookRes = Decrypt_Value(webhookRes.data, "vozo");
                    // console.log('TWILIO_PROVISION: update_webhook decrypted API response:', decryptedWebhookRes);

                    const webhookSuccess = decryptedWebhookRes && decryptedWebhookRes.status === "success";

                    if (!webhookSuccess) {
                      // console.error('TWILIO_PROVISION: Webhook update failed! Response:', decryptedWebhookRes);
                      setIncomingProvisionStatus({ step: 'done', phoneNumberSid: true, webhookUpdated: false, message: decryptedWebhookRes?.message || "Failed to update webhook." });
                      return;
                    }

                    // console.log('TWILIO_PROVISION: Incoming call provisioning successful!');
                    setIncomingProvisionStatus({
                      step: 'done',
                      phoneNumberSid: true,
                      webhookUpdated: true,
                      message: "Incoming call configured successfully"
                    });
                  } catch (err) {
                    // console.error('TWILIO_PROVISION: Network or server error during incoming provisioning:', err);
                    setIncomingProvisionStatus({ step: 'done', phoneNumberSid: false, webhookUpdated: false, message: "Failed to connect to backend API." });
                  }
                } else {
                  setIncomingProvisionStatus({ step: 'idle', phoneNumberSid: null, webhookUpdated: null, message: '' });
                }
              }} style={{ marginRight: "10px" }} />
              <span style={{ color: phoneCallEnabled ? "#555" : "#aaa" }}>Incoming Call</span>
            </div>

            {incomingEnabled && incomingProvisionStatus.step !== 'idle' && (
              <div style={{ marginLeft: "45px", marginTop: "10px", fontSize: "14px" }}>

                {/* Phone Number SID Status */}
                <div style={{ display: "flex", alignItems: "center", marginBottom: "6px" }}>
                  {incomingProvisionStatus.step === 'phone_number_sid_loading' ? <SpinnerIcon /> : incomingProvisionStatus.phoneNumberSid ? <CheckIcon /> : <CrossIcon />}
                  <span style={{ color: incomingProvisionStatus.step === 'phone_number_sid_loading' ? "#555" : incomingProvisionStatus.phoneNumberSid ? "#333" : "#F24822" }}>Phone Number SID</span>
                </div>

                {/* Webhook Status (Only show if Phone Number SID succeeded or is loading webhook) */}
                {(incomingProvisionStatus.phoneNumberSid || incomingProvisionStatus.step === 'update_webhook_loading') && (
                  <div style={{ display: "flex", alignItems: "center", marginBottom: "12px" }}>
                    {incomingProvisionStatus.step === 'update_webhook_loading' ? <SpinnerIcon /> : incomingProvisionStatus.webhookUpdated ? <CheckIcon /> : <CrossIcon />}
                    <span style={{ color: incomingProvisionStatus.step === 'update_webhook_loading' ? "#555" : incomingProvisionStatus.webhookUpdated ? "#333" : "#F24822" }}>Webhook Configuration</span>
                  </div>
                )}

                {/* Overall Status Message */}
                {incomingProvisionStatus.step === 'done' && (
                  <div style={{ fontSize: "12px", color: incomingProvisionStatus.phoneNumberSid && incomingProvisionStatus.webhookUpdated ? "#27ae60" : "#F24822" }}>
                    {incomingProvisionStatus.message}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <div className="integration-modal-footer">
        <button
          className="integration-cancel-btn"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </button>
        <button
          className="integration-cancel-btn"
          onClick={onClose}
          disabled={loading}
        >
          Skip
        </button>
        <button
          className="integration-connect-btn"
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? "Saving..." : "Save"}
        </button>
      </div>
    </Modal>
  );
}
